<template>
  <div class="spotlight-grid">
    <ol class="grid" :class="{ 'is--narrow': narrow }">
      <slot></slot>
      <li
        v-for="(article, index) in articles"
        :key="`teaser-grid-${article.id}`"
        class="grid-item manual-article"
        :class="{
          'is--spotlight-item': index === 0 && article.articleType !== 'shorts' && spotlight,
        }"
      >
        <component
          :is="getComponent(index, article)"
          v-bind="{ ...(article as ICollectionTeaser), ...imageLoadingProps(index) }"
          :size="index === 0 && article.articleType !== 'shorts' ? 'large' : 'regular'"
          aspect-ratio="4x3"
          aspect-ratio-block="16x9"
          :padded="!padded"
          :show-date="showDates"
          :analytics="analytics"
          :show-label="showLabel"
          :fluid="true"
          :_index="index"
        />
      </li>

      <li v-if="banner" class="grid-item banner-item" :class="{ 'is--padded': !padded }">
        <TeaserBanner
          v-bind="{ ...(banner as ITeaser), ...imageLoadingProps(articles?.length || 0) }"
          :is-presenter="isPresenter"
          :analytics="{
            linkAnalyticsTag: `${analytics?.linkAnalyticsTag || 'spotlight'}-banner`,
          }"
        />
      </li>
    </ol>
  </div>
</template>

<script setup lang="ts">
import type { ICmsBanners } from '~~/types/_cms-types';
import type { IAnalytics, ICollectionTeaser, ITeaser } from '~~/types/local_types';
import PosterBlock from '~/components/teaser/conditional/PosterBlock.vue';
import BlockListItem from '~/components/teaser/conditional/BlockListItem.vue';
import useImagePriority from '~/composables/useImagePriority';
import ShortPosterListItem from '~/components/teaser/conditional/ShortPosterListItem.vue';

interface IProps {
  articles: ITeaser[];
  banner?: ICmsBanners;
  priority?: 'high' | 'auto';
  padded?: boolean; // makes the component padded (padding left/right), if the component is padded the items are not, and vice versa
  spotlight?: boolean; // make the first item large
  showDates?: boolean;
  showLabel?: boolean;
  narrow?: boolean;
  analytics?: IAnalytics;
  isPresenter?: boolean;
}

const props = withDefaults(defineProps<IProps>(), {
  banner: undefined,
  priority: 'auto',
  padded: false,
  spotlight: false,
  showDates: false,
  showLabel: true,
  narrow: false,
  analytics: undefined,
  isPresenter: false,
});

const getComponent = (index: number, article: ITeaser) => {
  if (article.articleType === 'shorts') {
    return ShortPosterListItem;
  } else if (props.spotlight) {
    return index === 0 ? PosterBlock : BlockListItem;
  }

  return BlockListItem;
};

const imageLoadingProps = (index: number) => {
  return useImagePriority(props.priority, props.spotlight ? 2 : 3, index);
};
</script>

<style scoped lang="postcss">
.grid {
  overflow: visible;
  height: auto;
  --grid-template-columns: repeat(2, 1fr);
  --grid-gap: var(--unit-16);

  display: grid;
  grid-template-columns: var(--grid-template-columns);
  align-items: stretch;
  gap: var(--grid-gap);

  @media (--md-viewport) {
    --grid-gap: var(--unit-24) var(--unit-16);
    --grid-template-columns: repeat(4, 1fr);

    &.is--narrow {
      --grid-template-columns: repeat(3, 1fr);
    }
  }

  @media (--lg-viewport) {
    --grid-gap: var(--unit-24);
  }
}

.grid-item {
  position: relative;
}

.grid-item.is--spotlight-item {
  grid-column: span 2;

  &:deep(.label) {
    color: hsl(var(--color-secondary-500));

    @media (--md-viewport) {
      color: hsl(var(--color-primary-50));
    }
  }

  &:deep(.teaser-block) {
    .meta {
      --meta-padding: var(--unit-12) var(--unit-16) var(--unit-2) var(--unit-16);

      @media (--lg-viewport) {
        --meta-padding: var(--unit-20) var(--unit-24) var(--unit-26) var(--unit-24);
      }

      &:not(.is--padded) {
        --meta-padding: var(--unit-12) 0 var(--unit-2) 0;

        @media (--lg-viewport) {
          --meta-padding: var(--unit-20) 0 var(--unit-26) 0;
        }
      }
    }
  }

  &:deep(.teaser-poster) {
    .meta {
      --meta-padding: var(--unit-48) var(--unit-24) var(--unit-24) var(--unit-24);
    }
  }
}

.grid-item:not(.is--spotlight-item) {
  --grid-columns: span 2;
  grid-column: var(--grid-columns);
  @media (--md-viewport) {
    --grid-columns: span 1;
  }
}

.banner-item {
  &:deep(.meta) {
    --meta-padding: var(--unit-24) var(--unit-20);

    @media (--lg-viewport) {
      --meta-padding: var(--unit-24) var(--unit-24) var(--unit-32) var(--unit-24);
    }
  }
  &.is--padded {
    --banner-margin: 0 var(--unit-18);
    margin: var(--banner-margin);

    @media (--md-viewport) {
      --banner-margin: 0;
    }
  }
}
</style>
